import {
  Box,
  LinearProgress,
  Typography,
  linearProgressClasses,
} from '@mui/material';

import Lottie from 'react-lottie';
import sparkLoadingAnimation from 'animations/sparkLoading.json';
import { STATUS_TAGS } from 'constants/common';
import { useCompanyOverviewByID } from 'hooks/CompanyOverview/useCompanyOverviewByID';
import { useSparkRunsStatusTypes } from 'hooks/EntityTypes/useSparkRunsStatusTypes';
import { useSparkRunsQueueJob, useStartSparkRun } from 'hooks/SparkRuns';
import { useWorkspaceByID } from 'hooks/Workspace/useWorkspaceByID';
import { useWorkspaceContext } from 'hooks/common';
import React from 'react';
import { useQueryClient } from 'react-query';

const solutionSparkID = 'be1b034f-1dbc-4d21-a6e3-b8ded77f3e26';

const SolutionSection = () => {
  const { currentWorkspaceID } = useWorkspaceContext();
  const { companyOverview } = useWorkspaceByID(currentWorkspaceID);
  const {
    keyObjectives,
    mainChallenges,
    marketApproach,
    businessIndustry,
    legalStructure,
    businessStage,
    geographicMarket,
    country,
  } = useCompanyOverviewByID(companyOverview?.id);

  const [latestSparkRunID, setLatestSparkRunID] = React.useState<string | null>(
    null
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { mutateAsync: startSparkRun } = useStartSparkRun({
    sparkID: solutionSparkID,
  });
  const { sparkRunQueue } = useSparkRunsQueueJob(latestSparkRunID);

  const parameters = React.useMemo(() => {
    if (
      !companyOverview ||
      !keyObjectives ||
      !mainChallenges ||
      !businessIndustry ||
      !legalStructure ||
      !businessStage ||
      !geographicMarket ||
      !country ||
      !marketApproach
    ) {
      return;
    }
    return [
      {
        id: '4b883465-dafb-482b-ab3d-68c377013b21',
        name: 'company_overview_json',
        value: JSON.stringify({
          company_name: companyOverview.attributes.companyName,
          business_industry: businessIndustry.attributes.name,
          business_stage: businessStage.attributes.name,
          core_offering: companyOverview.attributes.coreOffering,
          country: country.attributes.name,
          market_approach: marketApproach.map(
            (marketApproach) => marketApproach.attributes.name
          ),
          geographic_market: geographicMarket.attributes.name,
          key_objectives: keyObjectives.map(
            (objective) => objective.attributes.name
          ),
          main_challenges: mainChallenges.map(
            (mainChallenge) => mainChallenge.attributes.name
          ),
          legal_structure: legalStructure.attributes.name,
          other_key_objectives: companyOverview.attributes.otherKeyObjectives,
          other_main_challenges: companyOverview.attributes.otherMainChallenges,
        }),
      },
    ];
  }, [
    businessIndustry,
    businessStage,
    companyOverview,
    country,
    geographicMarket,
    keyObjectives,
    legalStructure,
    mainChallenges,
    marketApproach,
  ]);

  const { sparkRunsStatusNameByIdMap } = useSparkRunsStatusTypes();
  const queryClient = useQueryClient();

  const { attributes: queueJobAttributes, meta } = sparkRunQueue || {};
  const { queueJob } = meta || {};
  const { sparkRunStatusTypeID } = queueJobAttributes || {};

  React.useEffect(() => {
    if (!parameters?.length || latestSparkRunID) {
      return;
    }
    setIsLoading(true);
    // startSparkRun({ parameters }).then((res) =>
    //   setLatestSparkRunID(res.data.id)
    // );
  }, [latestSparkRunID, parameters, startSparkRun]);

  React.useEffect(() => {
    if (
      !sparkRunQueue ||
      !sparkRunsStatusNameByIdMap ||
      !sparkRunStatusTypeID
    ) {
      return;
    }
    // need to update the run status when when each time queue job is fetched
    const isPending =
      sparkRunsStatusNameByIdMap[sparkRunStatusTypeID] === STATUS_TAGS.PENDING;
    const isRunning =
      sparkRunsStatusNameByIdMap[sparkRunStatusTypeID] === STATUS_TAGS.RUNNING;
    const isComplete = !isPending && !isRunning;
    if (isComplete) {
      queryClient.invalidateQueries('WelcomeGreetings');
      setIsLoading(false);
    }
  }, [
    queryClient,
    sparkRunQueue,
    sparkRunStatusTypeID,
    sparkRunsStatusNameByIdMap,
  ]);
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: sparkLoadingAnimation,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <div>
      {isLoading ? (
        <Box display='flex' flexDirection='column' alignItems='center' gap={2}>
          <Lottie options={defaultOptions} height={100} width={100} />

          <Typography
            color={(theme) => theme.palette.grey[800]}
            variant='subtitle1'
          >
            Generating solution for {companyOverview?.attributes.companyName}
          </Typography>
          <LinearProgress
            variant='determinate'
            sx={{
              width: '50%',
              height: '8px',
              borderRadius: '10px',
              [`& .${linearProgressClasses.bar}`]: {
                borderRadius: 50,
              },
            }}
            value={queueJob?.data.attributes.percentageComplete || 0}
          />
        </Box>
      ) : (
        ''
      )}
    </div>
  );
};
export default SolutionSection;
